import React, { useCallback, useMemo, useRef, useState } from "react";

import {
  FaAlignCenter,
  FaAlignLeft,
  FaAlignRight,
  FaBold,
  FaImage,
  FaItalic,
  FaTrash,
  FaUnderline
} from "react-icons/fa";
import { toastr } from "react-redux-toastr";
import { createEditor, Editor, Transforms } from "slate";
import { withHistory } from "slate-history";
import {
  Editable,
  ReactEditor,
  Slate,
  useFocused,
  useSelected,
  withReact
} from "slate-react";

import URL from "../../config/api";
import axios from "../../config/axios";

const emptyDocument = () => [{ type: "paragraph", children: [{ text: "" }] }];

export const parseFormularioContent = content => {
  if (Array.isArray(content)) return content;
  if (typeof content !== "string" || !content.trim()) return emptyDocument();
  try {
    const parsed = JSON.parse(content);
    return Array.isArray(parsed) ? parsed : emptyDocument();
  } catch (_error) {
    return [{ type: "paragraph", children: [{ text: content }] }];
  }
};

export const formularioContentHasValue = content => {
  const nodes = parseFormularioContent(content);
  const hasValue = items =>
    items.some(item =>
      item?.type === "image"
        ? Boolean(item.media_id && item.url)
        : typeof item?.text === "string"
        ? Boolean(item.text.trim())
        : Array.isArray(item?.children) && hasValue(item.children)
    );
  return hasValue(nodes);
};

const imageSrc = path =>
  /^(https?:\/\/|blob:|data:)/i.test(path || "")
    ? path
    : `${URL.img}${path || ""}`;

function ImageElement({
  attributes,
  children,
  element,
  editor,
  token,
  readOnly
}) {
  const selected = useSelected();
  const focused = useFocused();

  const align = value => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.setNodes(editor, { align: value }, { at: path });
  };

  const remove = async () => {
    const path = ReactEditor.findPath(editor, element);
    Transforms.removeNodes(editor, { at: path });
    if (!element.media_id) return;
    try {
      await axios.delete(`${URL.api}formulario/media/${element.media_id}`, {
        headers: { authorization: `Bearer ${token}` }
      });
    } catch (_error) {
      // Mídia já vinculada é preservada; o salvamento remove apenas seu uso.
    }
  };

  return (
    <span
      {...attributes}
      className={`formulario-rich-content__image-wrapper is-${
        element.align || "left"
      }`}
    >
      <span contentEditable={false}>
        <img
          src={imageSrc(element.url)}
          alt={element.alt || ""}
          className={selected && focused ? "is-selected" : ""}
        />
        {!readOnly && (
          <span className="formulario-rich-content__image-actions">
            {[
              ["left", "Alinhar à esquerda", FaAlignLeft],
              ["center", "Centralizar", FaAlignCenter],
              ["right", "Alinhar à direita", FaAlignRight]
            ].map(([value, label, Icon]) => (
              <button
                key={value}
                type="button"
                className={
                  (element.align || "left") === value ? "is-active" : ""
                }
                title={label}
                aria-label={label}
                onMouseDown={event => {
                  event.preventDefault();
                  align(value);
                }}
              >
                <Icon aria-hidden="true" />
              </button>
            ))}
            <button
              type="button"
              className="is-remove"
              title="Remover imagem"
              aria-label="Remover imagem"
              onMouseDown={event => {
                event.preventDefault();
                remove();
              }}
            >
              <FaTrash aria-hidden="true" />
            </button>
          </span>
        )}
      </span>
      {children}
    </span>
  );
}

export default function FormularioRichContent({
  value,
  onChange,
  token,
  readOnly = false,
  placeholder = "Digite o texto"
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const editor = useMemo(() => {
    const instance = withHistory(withReact(createEditor()));
    const { isVoid } = instance;
    instance.isVoid = element =>
      element.type === "image" ? true : isVoid(element);
    return instance;
  }, []);
  const editorValue = useMemo(() => parseFormularioContent(value), [value]);

  const renderElement = useCallback(
    props =>
      props.element.type === "image" ? (
        <ImageElement
          {...props}
          editor={editor}
          token={token}
          readOnly={readOnly}
        />
      ) : (
        <span
          className="formulario-rich-content__paragraph"
          {...props.attributes}
        >
          {props.children}
        </span>
      ),
    [editor, readOnly, token]
  );

  const renderLeaf = useCallback(({ attributes, children, leaf }) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;
    return <span {...attributes}>{children}</span>;
  }, []);

  const toggleMark = (event, mark) => {
    event.preventDefault();
    const marks = Editor.marks(editor);
    if (marks?.[mark]) Editor.removeMark(editor, mark);
    else Editor.addMark(editor, mark, true);
  };

  const uploadFile = async file => {
    if (!file) return;
    const uploadId = `${Date.now()}-${Math.random()}`;
    const previewUrl = window.URL.createObjectURL(file);
    const pendingImage = {
      type: "image",
      upload_id: uploadId,
      url: previewUrl,
      alt: file.name || "",
      align: "left",
      uploading: true,
      children: [{ text: "" }]
    };

    Transforms.insertNodes(editor, pendingImage);
    Transforms.insertNodes(editor, emptyDocument()[0]);

    const findPendingImage = () =>
      Array.from(
        Editor.nodes(editor, {
          at: [],
          match: node => node.upload_id === uploadId
        })
      )[0];

    const body = new FormData();
    body.append("file", file);
    setUploading(true);
    try {
      const response = await axios.post(`${URL.api}formulario/media`, body, {
        headers: {
          authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      const media = response.data.data;
      const pendingEntry = findPendingImage();
      if (pendingEntry) {
        Transforms.setNodes(
          editor,
          {
            media_id: media.id,
            url: media.path,
            alt: media.nome_original || ""
          },
          { at: pendingEntry[1] }
        );
        Transforms.unsetNodes(editor, ["upload_id", "uploading"], {
          at: pendingEntry[1]
        });
      }
    } catch (error) {
      const pendingEntry = findPendingImage();
      if (pendingEntry) {
        Transforms.removeNodes(editor, { at: pendingEntry[1] });
      }
      toastr.error(
        "Erro",
        error.response?.data?.error || "Não foi possível enviar a imagem."
      );
    } finally {
      window.URL.revokeObjectURL(previewUrl);
      setUploading(false);
    }
  };

  const upload = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    uploadFile(file);
  };

  const pasteImage = event => {
    const files = Array.from(event.clipboardData?.files || []);
    const itemFiles = Array.from(event.clipboardData?.items || [])
      .filter(item => item.kind === "file")
      .map(item => item.getAsFile())
      .filter(Boolean);
    const file = [...files, ...itemFiles].find(item =>
      item.type.startsWith("image/")
    );
    if (!file) return;
    event.preventDefault();
    uploadFile(file);
  };

  return (
    <div className={`formulario-rich-content ${readOnly ? "is-readonly" : ""}`}>
      <Slate
        editor={editor}
        value={editorValue}
        onChange={nodes => onChange?.(JSON.stringify(nodes))}
      >
        <Editable
          readOnly={readOnly}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder={placeholder}
          onPaste={readOnly ? undefined : pasteImage}
        />
        {!readOnly && (
          <div className="formulario-rich-content__toolbar">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={upload}
            />
            <button
              type="button"
              title="Negrito"
              aria-label="Negrito"
              onMouseDown={event => toggleMark(event, "bold")}
            >
              <FaBold aria-hidden="true" />
            </button>
            <button
              type="button"
              title="Itálico"
              aria-label="Itálico"
              onMouseDown={event => toggleMark(event, "italic")}
            >
              <FaItalic aria-hidden="true" />
            </button>
            <button
              type="button"
              title="Sublinhado"
              aria-label="Sublinhado"
              onMouseDown={event => toggleMark(event, "underline")}
            >
              <FaUnderline aria-hidden="true" />
            </button>
            <button
              type="button"
              disabled={uploading}
              title={uploading ? "Enviando imagem" : "Inserir imagem"}
              aria-label={uploading ? "Enviando imagem" : "Inserir imagem"}
              onMouseDown={event => {
                event.preventDefault();
                inputRef.current?.click();
              }}
            >
              <FaImage aria-hidden="true" />
            </button>
          </div>
        )}
      </Slate>
    </div>
  );
}
